// ./src/hooks/useUserProfile.ts
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import { UserProfile, UserProfileService, UserProfileInput } from "@/utils/api";
import { useApp } from "@/contexts";
import { ApiError } from "@/utils";

// ============================================
// Types
// ============================================

export interface UseUserProfileOptions {
  /** Auto-load data on mount (default: true) */
  autoLoad?: boolean;
}

export interface UseUserProfileReturn {
  // Data state
  userProfile: UserProfile | null;
  isLoading: boolean;
  isLoaded: boolean;

  /**
   * Indicates if profile exists in the database.
   * - `true` - profile definitively exists
   * - `false` - profile definitively does not exist (NOT_FOUND)
   * - `null` - unknown (initial state or error occurred)
   */
  profileExists: boolean | null;

  // Operations state
  isUpdating: boolean;

  // Core operations - return OpResult for immediate access to result
  get: (id?: string) => Promise<OpResult>;
  update: (params: UserProfileInput) => Promise<OpResult>;

  // Additional convenience methods
  refresh: () => Promise<OpResult>;

  // Helper operations
  reset: () => void;

  // Preference helpers
  getPreference: <T>(key: keyof UserProfile, defaultValue: T) => T;

  // Error handling - standardized ApiError with built-in checkers
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Hook Implementation
// ============================================

export const useUserProfile = (
  options: UseUserProfileOptions = {}
): UseUserProfileReturn => {
  const { autoLoad = true } = options;

  // Get userId directly from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  const previousUserIdRef = useRef<string | null | undefined>(undefined);
  const userIdRef = useRef<string | null>(userId);

  // Update userIdRef when userId changes
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // Data state
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [profileExists, setProfileExists] = useState<boolean | null>(null);

  // Operations state
  const [isUpdating, setIsUpdating] = useState(false);

  // Error handling - now uses standardized ApiError
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setUserProfile(null);
    setLastError(null);
    setIsLoading(false);
    setIsLoaded(false);
    setProfileExists(null);
    setIsUpdating(false);
  }, []);

  // Get user profile (matching API method name)
  // Accepts optional id parameter to override current user (e.g., for admin scenarios)
  // Returns OpResult for immediate access to result
  const get = useCallback(async (id?: string): Promise<OpResult> => {
    const targetId = id || userIdRef.current;

    if (!targetId) {
      const result = new OpResult()
        .setCode(OP_RESULT_CODES.VALIDATION_FAILED)
        .addError("userId", "User ID is required to load profile");
      setLastError(ApiError.fromResult(result, "User ID is required to load profile"));
      setIsLoaded(true);
      return result;
    }

    setIsLoading(true);
    setLastError(null);

    try {
      const result = await UserProfileService.get(targetId);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = Array.isArray(result.data)
          ? (result.data as UserProfile[])[0]
          : (result.data as UserProfile);
        setUserProfile(data);
        setProfileExists(true);
        setIsLoaded(true);
        setLastError(null);
        return result;
      }

      if (result.code === OP_RESULT_CODES.NOT_FOUND) {
        // Profile doesn't exist yet - this is valid for new users
        // This is NOT an error state, just a fact about new users
        setUserProfile(null);
        setProfileExists(false);
        setIsLoaded(true);
        setLastError(null);
        return result;
      }

      // Error occurred - preserve previous profileExists state, set error
      setLastError(ApiError.fromResult(result, "Failed to load user profile"));
      setUserProfile(null);
      // Don't change profileExists - we don't know the true state
      setIsLoaded(true);
      return result;
    } catch (error) {
      // Exception occurred - preserve previous profileExists state, set error
      const result = OpResult.fromException(error);
      setLastError(ApiError.fromException(error, "Failed to load user profile"));
      setUserProfile(null);
      // Don't change profileExists - we don't know the true state
      setIsLoaded(true);
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update user profile (matching API method name)
  // Returns OpResult for immediate access to result
  const update = useCallback(
    async (params: UserProfileInput): Promise<OpResult> => {
      const targetId = userIdRef.current;

      if (!targetId) {
        const result = new OpResult()
          .setCode(OP_RESULT_CODES.VALIDATION_FAILED)
          .addError("userId", "User ID is required to update profile");
        setLastError(ApiError.fromResult(result, "User ID is required to update profile"));
        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await UserProfileService.update(
          { id: targetId },
          params
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedProfile = Array.isArray(result.data)
            ? (result.data as UserProfile[])[0]
            : (result.data as UserProfile);
          setUserProfile(updatedProfile);
          setProfileExists(true);
          setLastError(null);
          return result;
        }

        // Error occurred - don't change profileExists state
        setLastError(ApiError.fromResult(result, "Failed to update user profile"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to update user profile"));
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  // Refresh user profile
  const refresh = useCallback(async (): Promise<OpResult> => {
    return get();
  }, [get]);

  // Helper to get a preference value with default
  const getPreference = useCallback(
    <T>(key: keyof UserProfile, defaultValue: T): T => {
      if (!userProfile) return defaultValue;
      const value = userProfile[key];
      return (
        value !== undefined && value !== null ? value : defaultValue
      ) as T;
    },
    [userProfile]
  );

  // Handle user change - reset and reload data when userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        get(userId);
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        get(userId);
      }
    }
  }, [userId, autoLoad, get, reset]);

  // Memoize the return object to prevent unnecessary re-renders of consumers
  return useMemo(
    () => ({
      userProfile,
      isLoading,
      isLoaded,
      profileExists,
      isUpdating,
      get,
      update,
      refresh,
      reset,
      getPreference,
      lastError,
      clearError,
    }),
    [
      userProfile,
      isLoading,
      isLoaded,
      profileExists,
      isUpdating,
      get,
      update,
      refresh,
      reset,
      getPreference,
      lastError,
      clearError,
    ]
  );
};